/**
 * Which steps the booking wizard has, and which of them can be jumped back to.
 *
 * Pulled out of BookingFlow as a pure function because the wizard has no stored
 * step machine — the rail, the back button and `next()` are all derived from
 * this list, so a wrong answer here does not render oddly, it silently changes
 * where "Continue" goes. That is precisely how the specialist step came to be
 * skipped: the staff list is a round trip, and while it was in flight the flow
 * could not tell "we have not asked yet" from "this salon has nobody", so a
 * customer who tapped a service and Continue in quick succession went straight
 * to the time picker.
 */

export type StepKey = 'branch' | 'services' | 'staff' | 'time' | 'review';

export interface StepDescriptor {
  key: StepKey;
  label: string;
  /** Reachable by tapping the rail (as opposed to only via Continue/Back). */
  done: boolean;
}

export interface StepInputs {
  /** A tenant with one published branch never sees the location step. */
  singleBranch: boolean;
  branchChosen: boolean;
  serviceCount: number;
  /** Tenant setting: may a customer pick their specialist at all? */
  allowStaffSelection: boolean;
  /**
   * Whether `staffCount` describes the CURRENT branch + service selection.
   * False while the request is in flight — which must not be read as "nobody".
   */
  staffResolved: boolean;
  staffCount: number;
  timeChosen: boolean;
  labels: Record<StepKey, string>;
}

/**
 * The specialist step is included whenever the tenant allows choosing and we
 * have not been told, for this exact selection, that there is nobody to choose
 * between. Unknown counts as "show it": an extra step that then offers "any
 * available" is a small cost, whereas dropping a step the customer expected is
 * a booking they did not mean to make.
 */
export function staffStepVisible(input: Pick<StepInputs, 'allowStaffSelection' | 'staffResolved' | 'staffCount'>): boolean {
  if (!input.allowStaffSelection) return false;
  if (!input.staffResolved) return true;
  return input.staffCount > 0;
}

export function buildSteps(input: StepInputs): StepDescriptor[] {
  const steps: StepDescriptor[] = [];
  if (!input.singleBranch) {
    steps.push({ key: 'branch', label: input.labels.branch, done: input.branchChosen });
  }
  steps.push({ key: 'services', label: input.labels.services, done: input.serviceCount > 0 });
  if (staffStepVisible(input)) {
    // Reachable as soon as services exist: a null employee legitimately means
    // "any available", so there is no later signal that the step was visited,
    // and gating it on a chosen time made the rail a one-way trip.
    steps.push({ key: 'staff', label: input.labels.staff, done: input.serviceCount > 0 });
  }
  steps.push({ key: 'time', label: input.labels.time, done: input.timeChosen });
  steps.push({ key: 'review', label: input.labels.review, done: false });
  return steps;
}

/** The step after `current`, or null at the end of the flow. */
export function nextStep(steps: StepDescriptor[], current: StepKey): StepKey | null {
  const index = steps.findIndex((s) => s.key === current);
  return index >= 0 ? (steps[index + 1]?.key ?? null) : null;
}

/** The step before `current`, or null at the start of the flow. */
export function previousStep(steps: StepDescriptor[], current: StepKey): StepKey | null {
  const index = steps.findIndex((s) => s.key === current);
  return index > 0 ? (steps[index - 1]?.key ?? null) : null;
}
